/**
 * Global piles initialization for Legendary Arena setup.
 *
 * Constructs typed GlobalPiles from MatchSetupConfig count fields.
 * This is a setup-time helper only — no gameplay logic, no validation.
 *
 * No boardgame.io imports allowed — this is a pure helper module.
 */
import { shuffleDeck } from './shuffle.js';
// why: Global pile cards (bystanders, wounds, officers, sidekicks) are
// generic game components — each pile contains identical copies of a single
// token card type. Token ext_ids use the "pile-" prefix convention rather
// than registry ext_ids because these are standard game components that
// exist in every Legendary game, not set-specific cards.
/** Well-known ext_id for Bystander pile cards. */
export const BYSTANDER_EXT_ID = 'pile-bystander';
/** Well-known ext_id for Wound pile cards. */
export const WOUND_EXT_ID = 'pile-wound';
/** Well-known ext_id for S.H.I.E.L.D. Officer pile cards. */
export const SHIELD_OFFICER_EXT_ID = 'pile-shield-officer';
// why: D-24296 — the two starting-deck S.H.I.E.L.D. ext_ids live here beside the
// other well-known basic-S.H.I.E.L.D. / pile ext_ids (Officer above) so a single
// neutral leaf module owns them all. buildInitialGameState still owns the starting
// DECK composition; it re-exports these for back-compat. Relocated from
// buildInitialGameState so the villain-effect handler can import all three basic
// S.H.I.E.L.D. ext_ids from one place without importing the setup orchestrator.
/** Well-known ext_id for S.H.I.E.L.D. Agent starting cards (recruit 1). */
export const SHIELD_AGENT_EXT_ID = 'starting-shield-agent';
/** Well-known ext_id for S.H.I.E.L.D. Trooper starting cards (attack 1). */
export const SHIELD_TROOPER_EXT_ID = 'starting-shield-trooper';
/** Well-known ext_id for Sidekick pile cards. */
export const SIDEKICK_EXT_ID = 'pile-sidekick';
/**
 * Creates an array of identical CardExtId entries.
 *
 * @param extId - The ext_id string to repeat.
 * @param count - How many copies to create.
 * @returns Array of `count` copies of `extId`.
 */
function createPileCards(extId, count) {
    const cards = [];
    for (let i = 0; i < count; i++) {
        cards.push(extId);
    }
    return cards;
}
/**
 * Builds the global piles from config count fields.
 *
 * Each pile is sized from the corresponding MatchSetupConfig count field
 * and shuffled using the deterministic RNG.
 *
 * @param config - Validated match setup config with count fields.
 * @param context - Shuffle provider with deterministic RNG.
 * @returns GlobalPiles with shuffled arrays of CardExtId strings.
 */
export function buildGlobalPiles(config, context) {
    // why: Each pile is shuffled for determinism consistency, even though
    // all cards in a given pile share the same ext_id. This ensures the
    // shuffle path is always exercised and the RNG state advances uniformly.
    return {
        bystanders: shuffleDeck(createPileCards(BYSTANDER_EXT_ID, config.bystandersCount), context),
        wounds: shuffleDeck(createPileCards(WOUND_EXT_ID, config.woundsCount), context),
        officers: shuffleDeck(createPileCards(SHIELD_OFFICER_EXT_ID, config.officersCount), context),
        sidekicks: shuffleDeck(createPileCards(SIDEKICK_EXT_ID, config.sidekicksCount), context),
        // why: empty — no scheme currently populates the Horrors pile. Future
        // scheme WPs will add population logic for schemes that use Horrors.
        horrors: [],
    };
}
//# sourceMappingURL=pilesInit.js.map